/**
 * AdminBugReports.js — Command Centre: review teen bug reports, acknowledge,
 * and award the unique Bug Hunter NFT once fixed. Also credit teen wallets.
 */
import React, { useEffect, useState } from 'react';
import { Bug, Check, X, Trophy, Loader2, PoundSterling } from 'lucide-react';
import { apiClient } from '../../api/client';

const AdminBugReports = () => {
  const [reports, setReports] = useState(null);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(null);

  const load = React.useCallback(async () => {
    try {
      const d = await apiClient.request(`/api/bug-bounty/admin/list?status=${filter}`);
      setReports(d?.reports || []);
    } catch (e) { setReports([]); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setBusy(id);
    try {
      if (action === 'award') {
        const r = await apiClient.request(`/api/bug-bounty/admin/${id}/award`, { method: 'POST' });
        alert(`Bug Hunter #${String(r.serial).padStart(3, '0')} awarded!`);
      } else {
        const note = action === 'rejected' ? (window.prompt('Reason (sent to the teen):') || '') : '';
        await apiClient.request(`/api/bug-bounty/admin/${id}/status`, {
          method: 'POST', body: JSON.stringify({ status: action, note }),
        });
      }
      load();
    } catch (e) { alert(e?.message || 'Action failed'); }
    finally { setBusy(null); }
  };

  const creditWallet = async (userId) => {
    const amt = window.prompt('Credit amount in £ (e.g. 12.50):');
    if (!amt) return;
    const pence = Math.round(parseFloat(amt) * 100);
    if (!pence || pence <= 0) return alert('Invalid amount');
    const reason = window.prompt('Reason shown to the teen:', 'NFT sale proceeds') || 'NFT sale proceeds';
    try {
      await apiClient.request('/api/nft/wallet/credit', {
        method: 'POST', body: JSON.stringify({ user_id: userId, amount_pence: pence, reason }),
      });
      alert(`£${(pence / 100).toFixed(2)} credited.`);
    } catch (e) { alert(e?.message || 'Credit failed'); }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 lg:p-10">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-purple-600 text-white flex items-center justify-center"><Bug size={20} /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bug Bounty Reports</h1>
            <p className="text-sm text-gray-500">Acknowledge → fix → award the Bug Hunter NFT.</p>
          </div>
        </div>
        <div className="flex gap-2 mb-5">
          {['all', 'new', 'acknowledged', 'fixed_awarded', 'rejected'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold ${filter === f ? 'bg-purple-600 text-white' : 'bg-white border border-gray-200 text-gray-600'}`}
              data-testid={`bug-filter-${f}`}>
              {f.replace('_', ' ')}
            </button>
          ))}
        </div>
        {reports === null ? (
          <Loader2 className="animate-spin text-purple-500" size={26} />
        ) : reports.length === 0 ? (
          <p className="text-sm text-gray-400 py-10 text-center bg-white rounded-2xl border border-gray-100">No reports here.</p>
        ) : (
          <ul className="space-y-4">
            {reports.map(r => (
              <li key={r.report_id} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm" data-testid={`admin-bug-${r.report_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{r.reporter_name || r.user_id}</p>
                    <p className="text-xs text-gray-400 mb-2">{new Date(r.created_at).toLocaleString()} · {r.page || '—'} · <span className="font-bold">{r.status}</span></p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{r.description}</p>
                  </div>
                  {r.screenshot_data_url && (
                    <img src={r.screenshot_data_url} alt="bug screenshot" className="w-36 rounded-lg border border-gray-200 flex-shrink-0" />
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {r.status === 'new' && (
                    <>
                      <button onClick={() => act(r.report_id, 'acknowledged')} disabled={busy === r.report_id}
                        className="px-4 py-2 rounded-xl bg-amber-500 text-white text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid={`bug-ack-${r.report_id}`}>
                        <Check size={13} /> Acknowledge
                      </button>
                      <button onClick={() => act(r.report_id, 'rejected')} disabled={busy === r.report_id}
                        className="px-4 py-2 rounded-xl bg-gray-100 text-gray-600 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid={`bug-reject-${r.report_id}`}>
                        <X size={13} /> Not a bug
                      </button>
                    </>
                  )}
                  {(r.status === 'new' || r.status === 'acknowledged') && (
                    <button onClick={() => act(r.report_id, 'award')} disabled={busy === r.report_id}
                      className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid={`bug-award-${r.report_id}`}>
                      <Trophy size={13} /> Fixed — Award NFT
                    </button>
                  )}
                  <button onClick={() => creditWallet(r.user_id)}
                    className="px-4 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 text-xs font-bold inline-flex items-center gap-1.5" data-testid={`bug-credit-${r.report_id}`}>
                    <PoundSterling size={13} /> Credit wallet
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminBugReports;
